/**
 * EID-61: classification of spec JSON-Patch operations by whether they can
 * change the spec's node *structure* — add, remove, move or rename nodes —
 * as opposed to only changing scalar content in place.
 *
 * Derived-node source location (an id lookup over the whole spec) only needs
 * to re-run after a structural change; content-only patches (e.g. toggling a
 * layer's `visible`, editing a label) can never move the source node. The
 * classification is deliberately conservative: any op that *could* introduce
 * or relocate a node counts as structural.
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note.
 */

export interface SpecPatchOp {
  op: string;
  path: string;
  value?: unknown;
}

export const isStructuralPatchOp = (op: SpecPatchOp): boolean => {
  // Inserting, deleting, moving or copying can always add/remove/relocate
  // nodes (array element ops shift sibling indices too).
  if (op.op !== 'replace') return true;
  // Replacing an `id` renames a node — id lookups must re-run.
  if (op.path === '/id' || op.path.endsWith('/id')) return true;
  // Replacing with an object or array can introduce/remove nodes anywhere
  // beneath the target path.
  return typeof op.value === 'object' && op.value !== null;
};

/** True when any op in the patch is structural. */
export const hasStructuralPatchOp = (ops: SpecPatchOp[]): boolean =>
  ops.some(isStructuralPatchOp);
